import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CircularProgress, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { MainContentArea } from '@/components/layout/MainContentArea'
import { ImageLightbox } from '@/components/content/ImageLightbox'
import { useContentBundle } from '@/hooks/useContentBundle'
import { useIsBelowMd } from '@/hooks/useLayoutMode'
import { useMediaBlobUrl } from '@/hooks/useMediaBlobUrl'
import { useSvgAspectRatio } from '@/hooks/useSvgAspectRatio'
import { formatFileSize } from '@/utils/mediaUtils'
import { galleryImages, mediaLabel } from '@/utils/mediaGallery'
import type { BundleMediaAsset } from '@/types/api'

function MediaNavItem({ asset, active, onSelect }: { asset: BundleMediaAsset; active: boolean; onSelect: () => void }) {
  return (
    <button
      className={`matter-nav-item media-gallery-nav-item${active ? ' active' : ''}`}
      aria-current={active ? 'true' : undefined}
      onClick={onSelect}
    >
      <span className="media-gallery-nav-label">{mediaLabel(asset)}</span>
      {asset.altText && (
        <span className="media-gallery-nav-alt" title={asset.altText}>
          {asset.altText}
        </span>
      )}
    </button>
  )
}

function MediaPreview({ asset }: { asset: BundleMediaAsset }) {
  const { t } = useTranslation()
  const { url, state } = useMediaBlobUrl(asset.systemFileName)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const isSvg = asset.mimeType === 'image/svg+xml'
  const svgRatio = useSvgAspectRatio(isSvg ? url : null)

  if (state === 'loading') {
    return (
      <div className="media-gallery-status">
        <CircularProgress size={32} />
      </div>
    )
  }

  if (state === 'error' || !url) {
    return (
      <div className="media-gallery-status">
        <Typography color="error">{t('mediaGallery.loadFailed')}</Typography>
      </div>
    )
  }

  // An SVG with only a viewBox has no intrinsic size, so its box is sized from the aspect ratio
  // to hug the drawing at up to 70vh tall.
  const svgStyle = isSvg && svgRatio
    ? { aspectRatio: String(svgRatio), width: `min(100%, calc(70vh * ${svgRatio}))` }
    : undefined

  return (
    <>
      <button
        type="button"
        className="media-gallery-image-button"
        onClick={() => setLightboxOpen(true)}
        aria-label={t('mediaGallery.enlarge')}
      >
        <img
          className={`media-gallery-image${isSvg && !svgRatio ? ' media-gallery-image--svg' : ''}`}
          style={svgStyle}
          src={url}
          alt={asset.altText ?? ''}
          title={asset.altText ?? undefined}
        />
      </button>
      <ImageLightbox
        src={lightboxOpen ? url : null}
        alt={asset.altText ?? undefined}
        mimeType={asset.mimeType}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  )
}

export function MediaGalleryPage() {
  const { t } = useTranslation()
  const { data: bundle, isLoading } = useContentBundle()
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [listOpen, setListOpen] = useState(false)
  const isBelowMd = useIsBelowMd()

  const media = useMemo(() => galleryImages(bundle?.media), [bundle?.media])

  if (isLoading) {
    return (
      <MainContentArea>
        <div className="loading-state">{t('pages.loading')}</div>
      </MainContentArea>
    )
  }

  if (media.length === 0) {
    return (
      <MainContentArea>
        <p className="empty-state">{t('mediaGallery.empty')}</p>
      </MainContentArea>
    )
  }

  const selected = media.find((m) => m.systemFileName === selectedName) ?? media[0]
  const select = (asset: BundleMediaAsset) => {
    setSelectedName(asset.systemFileName)
    setListOpen(false)
  }

  return (
    <MainContentArea>
      <div className="matter-layout media-gallery">
        {isBelowMd ? (
          <nav className="matter-nav matter-nav--list" aria-label={t('mediaGallery.navLabel')}>
            <button
              className="matter-nav-summary"
              onClick={() => setListOpen((o) => !o)}
              aria-expanded={listOpen}
              aria-controls="media-gallery-items"
            >
              <span className="matter-nav-summary-label">{mediaLabel(selected)}</span>
              <ExpandMoreIcon
                className={`matter-nav-summary-chevron${listOpen ? ' matter-nav-summary-chevron--open' : ''}`}
                fontSize="small"
              />
            </button>
            {listOpen && (
              <div id="media-gallery-items" className="matter-nav-items">
                {media.map((asset) => (
                  <MediaNavItem
                    key={asset.systemFileName}
                    asset={asset}
                    active={asset.systemFileName === selected.systemFileName}
                    onSelect={() => select(asset)}
                  />
                ))}
              </div>
            )}
          </nav>
        ) : (
          <nav className="matter-nav" aria-label={t('mediaGallery.navLabel')}>
            {media.map((asset) => (
              <MediaNavItem
                key={asset.systemFileName}
                asset={asset}
                active={asset.systemFileName === selected.systemFileName}
                onSelect={() => select(asset)}
              />
            ))}
          </nav>
        )}

        <div className="matter-content media-gallery-content">
          <h2 className="section-heading">{mediaLabel(selected)}</h2>
          <Typography variant="body2" color="text.secondary" className="media-gallery-meta">
            {selected.originalFileName} · {formatFileSize(selected.fileSize)}
          </Typography>
          <div className="media-gallery-preview">
            <MediaPreview key={selected.systemFileName} asset={selected} />
          </div>
        </div>
      </div>
    </MainContentArea>
  )
}
